import { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatPrice } from '@/utils/format';
import { estimateMonthlyPayment } from '@/utils/mortgage';

const COLORS = ['#007AFF', '#FF9500', '#3395FF', '#FF9500'];

export const MortgageCalculator = ({ price = 0, hoa = 0, variant = 'card' }) => {
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);

  const result = useMemo(() => {
    const down = (price * downPct) / 100;
    const principal = Math.max(0, price - down);
    const monthlyRate = rate / 100 / 12;
    const n = term * 12;
    const pi =
      monthlyRate === 0
        ? principal / n
        : (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
    const tax = (price * 0.011) / 12;
    const insurance = 1200 / 12;
    const total = estimateMonthlyPayment({ price, downPct, rate, term, hoa });
    return { down, principal, pi, tax, insurance, total };
  }, [price, downPct, rate, term, hoa]);

  const data = [
    { name: 'Principal & Interest', value: Math.round(result.pi) },
    { name: 'Property Tax', value: Math.round(result.tax) },
    { name: 'Home Insurance', value: Math.round(result.insurance) },
    { name: 'HOA', value: Math.round(hoa || 0) },
  ].filter((d) => d.value > 0);

  if (variant === 'compact') {
    return (
      <div data-testid="mortgage-calculator" className="space-y-6">
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Est. monthly payment</p>
          <p className="mt-1 font-display text-3xl font-bold text-slate-900" data-testid="mortgage-total">{formatPrice(Math.round(result.total))}</p>
        </div>

        <div className="relative mx-auto" style={{ width: 180, height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={50} outerRadius={72} paddingAngle={2} stroke="none">
                {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${formatPrice(v)}/mo`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600"><span className="h-3 w-3 rounded-full shrink-0" style={{ background: COLORS[i] }} />{d.name}</span>
              <span className="font-medium text-slate-900">{formatPrice(d.value)}/mo</span>
            </div>
          ))}
        </div>

        <div className="space-y-4 pt-2">
          <div>
            <div className="flex items-center justify-between"><Label className="text-sm font-medium text-slate-700">Down payment</Label><span className="text-sm font-semibold text-slate-900">{downPct}% · {formatPrice(result.down)}</span></div>
            <Slider className="mt-3" min={0} max={60} step={1} value={[downPct]} onValueChange={([v]) => setDownPct(v)} data-testid="mortgage-down-slider" />
          </div>
          <div>
            <Label htmlFor="rate-compact" className="text-sm font-medium text-slate-700">Interest rate (%)</Label>
            <Input id="rate-compact" type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="mt-2 rounded-xl" data-testid="mortgage-rate-input" />
          </div>
          <div>
            <Label htmlFor="term-compact" className="text-sm font-medium text-slate-700">Loan term (yrs)</Label>
            <Input id="term-compact" type="number" value={term} onChange={(e) => setTerm(Number(e.target.value))} className="mt-2 rounded-xl" data-testid="mortgage-term-input" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mortgage-calculator" className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between"><Label className="text-sm font-medium text-slate-700">Down payment</Label><span className="text-sm font-semibold text-slate-900">{downPct}% · {formatPrice(result.down)}</span></div>
            <Slider className="mt-3" min={0} max={60} step={1} value={[downPct]} onValueChange={([v]) => setDownPct(v)} data-testid="mortgage-down-slider" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rate" className="text-sm font-medium text-slate-700">Interest rate (%)</Label>
              <Input id="rate" type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="mt-2 rounded-xl" data-testid="mortgage-rate-input" />
            </div>
            <div>
              <Label htmlFor="term" className="text-sm font-medium text-slate-700">Loan term (yrs)</Label>
              <Input id="term" type="number" value={term} onChange={(e) => setTerm(Number(e.target.value))} className="mt-2 rounded-xl" data-testid="mortgage-term-input" />
            </div>
          </div>
          <div className="space-y-2">
            {data.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600"><span className="h-3 w-3 rounded-full" style={{ background: COLORS[i] }} />{d.name}</span>
                <span className="font-medium text-slate-900">{formatPrice(d.value)}/mo</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex flex-col items-center justify-center">
          <div className="h-52 w-full" style={{ minWidth: 200 }}>
            <ResponsiveContainer width="99%" height={208} minWidth={200} debounce={50}>
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={60} outerRadius={85} paddingAngle={2} stroke="none">
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${formatPrice(v)}/mo`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="pointer-events-none absolute top-1/2 -translate-y-[calc(50%+14px)] text-center">
            <p className="text-xs text-slate-500">Est. monthly</p>
            <p className="font-display text-2xl font-bold text-slate-900" data-testid="mortgage-total">{formatPrice(Math.round(result.total))}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
